import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, AlertTriangle, RefreshCw, Copy, ListPlus } from 'lucide-react';
import { StockSwapWallet } from './StockSwapWallet';
import { SellWallPanel } from './SellWallPanel';
import { AdminBaseSend } from './AdminBaseSend';
import { PoolPositionsPanel } from './PoolPositionsPanel';
import { CashOutTracker } from './CashOutTracker';
const USDC_BASE = '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913';

const B = process.env.REACT_APP_BACKEND_URL;
const LIST_URL = `${B}/api/token-list/beth-usd.json`;

const Check = ({ c }) => {
  const Icon = c.ok ? CheckCircle2 : c.warn_only ? AlertTriangle : XCircle;
  const color = c.ok ? 'text-emerald-400' : c.warn_only ? 'text-amber-400' : 'text-red-400';
  return (
    <div className="flex items-start gap-2 bg-slate-900/70 border border-slate-700 rounded-lg p-3" data-testid={`swap-check-${c.id}`}>
      <Icon className={`w-4 h-4 mt-0.5 ${color}`} />
      <div><p className="text-sm text-white">{c.label}</p><p className="text-[11px] text-slate-400 font-mono">{c.detail}</p></div>
    </div>
  );
};

const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success('Copied')).catch(() => toast.error('Copy failed'));

export const SwapReadinessCard = ({ token }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setBusy(true);
    axios.get(`${B}/api/pas-treasury-check/admin/stablecoin/swap-readiness`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setD(r.data)).catch(e => toast.error(e.response?.data?.detail?.toString() || 'Could not read chain')).finally(() => setBusy(false));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 space-y-4" data-testid="swap-readiness-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <img src={`${B}/api/token-list/beth-usd.png`} alt="BETH USD" className="w-10 h-10 rounded-full" data-testid="beth-usd-logo" />
          <div><p className="text-white font-bold">Swap readiness · BETH USD on Base</p>
            <p className="text-[11px] text-slate-400 font-mono break-all">{d?.wallet || '…'}</p></div>
        </div>
        <div className="flex items-center gap-2">
          {d && <span className={`text-xs px-2 py-1 rounded font-semibold ${d.ready ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`} data-testid="swap-ready-badge">{d.ready ? 'Ready to swap' : 'Not ready'}</span>}
          <button onClick={load} disabled={busy} className="p-2 text-slate-300 hover:text-white" data-testid="swap-readiness-refresh"><RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} /></button>
        </div>
      </div>
      {d && <div className="grid sm:grid-cols-2 gap-2">{d.checks.map(c => <Check key={c.id} c={c} />)}</div>}
      {d?.pool?.explorer_url && <a href={d.pool.explorer_url} target="_blank" rel="noreferrer" className="text-xs text-cyan-300 underline" data-testid="swap-pool-link">View pool on BaseScan</a>}
      <StockSwapWallet token={token} onDone={load} />
      <SellWallPanel token={token} onDone={load} />
      <PoolPositionsPanel token={token} onDone={load} />
      <AdminBaseSend token={token} onDone={load} />
      <CashOutTracker token={token} />
      <div className="border-t border-slate-700 pt-3 space-y-2">
        <p className="text-sm text-white font-semibold flex items-center gap-2"><ListPlus className="w-4 h-4 text-emerald-400" /> Find BETH USD in Uniswap search</p>
        <div className="flex gap-2">
          <input readOnly value={LIST_URL} className="flex-1 px-3 py-2 bg-slate-950 border border-slate-600 rounded text-slate-200 text-xs font-mono" data-testid="token-list-url" />
          <button onClick={() => copy(LIST_URL)} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs flex items-center gap-1" data-testid="token-list-copy"><Copy className="w-3 h-3" /> Copy</button>
        </div>
        <p className="text-[11px] text-slate-400">Uniswap's search only shows tokens on its curated lists, so BETH USD won't appear by name until it's listed (e.g. via CoinGecko). Until then buyers can use the direct link below, paste the token address, or import this list (token selector → Manage → Lists). Token address: <button onClick={() => d && copy(d.token)} className="font-mono text-cyan-300" data-testid="token-address-copy">{d?.token || '…'}</button></p>
        {d && <div className="flex gap-2">
          <input readOnly value={`https://app.uniswap.org/swap?chain=base&inputCurrency=${USDC_BASE}&outputCurrency=${d.token}`} className="flex-1 px-3 py-2 bg-slate-950 border border-slate-600 rounded text-slate-200 text-xs font-mono" data-testid="uniswap-buy-link" />
          <button onClick={() => copy(`https://app.uniswap.org/swap?chain=base&inputCurrency=${USDC_BASE}&outputCurrency=${d.token}`)} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs flex items-center gap-1" data-testid="uniswap-buy-copy"><Copy className="w-3 h-3" /> Copy buy link</button>
        </div>}
      </div>
    </div>
  );
};
